import { useCallback, useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';

import type { DeploymentStatus } from '@/shared/api/deploy';

interface UseDeploymentModalStateParams {
  open: boolean;
  mode: 'deploy' | 'start' | 'stop';
  currentStatus: DeploymentStatus | null;
  onLogAdd?: (log: string) => void;
}

export const useDeploymentModalState = ({ open, mode, currentStatus, onLogAdd }: UseDeploymentModalStateParams) => {
  const [isDeploying, setIsDeploying] = useState(false);

  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevOpenRef = useRef(open);
  const hasRefetchedAfterPackagingRef = useRef(false);

  const addLog = useCallback(
    (message: string) => {
      const timestamp = dayjs().format('HH:mm:ss');
      const formattedLog = `[${timestamp}] ${message}`;
      onLogAdd?.(formattedLog);
    },
    [onLogAdd]
  );

  useEffect(() => {
    const justOpened = !prevOpenRef.current && open;
    prevOpenRef.current = open;

    if (!justOpened) return;

    hasRefetchedAfterPackagingRef.current = false;

    if (mode === 'deploy') {
      setIsDeploying(false);
    }
  }, [open, mode]);

  useEffect(() => {
    if (!open || !isDeploying) return;

    const isFailed = currentStatus === 'deployment_failed' || currentStatus === 'serving_failed';
    const isCompleted = currentStatus === 'serving_active';

    if (isFailed || isCompleted) {
      setIsDeploying(false);
    }
  }, [open, isDeploying, currentStatus]);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    setIsDeploying(false);
    hasRefetchedAfterPackagingRef.current = false;
    clearCloseTimer();
  }, [clearCloseTimer]);

  return {
    isDeploying,
    setIsDeploying,
    closeTimerRef,
    hasRefetchedAfterPackagingRef,
    addLog,
    resetState,
    clearCloseTimer,
  };
};
